// Added by Sejune Oh on 2019-07-05
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, RefObject } from 'react'
import { WithStyles, withStyles, createStyles, WithTheme, Theme, Fade } from '@material-ui/core'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
    ReactEmptyEvent,
    MgmtValidator,
    FormatterProps,
    UILanguageProps,
    ReactSelectEvent,
    ReactFormEvent,
} from '@/helper/define'

// Intl
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Componenets
import Input from '@/components/common/Input'
import { SnackbarContent, Snackbar, Button } from '@/components/_primes'
import Select from '@/components/common/Select'
import MgmtConfigFormStyle from '@/styles/management/MgmtSubConfigFormStyle'
import { SNACK_BAR_VALID_TIME } from '@/styles/Theme'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface SystemAdminAddFCProps extends MgmtValidator, WithStyles<typeof styles>, WithTheme, FormatterProps {
    formId: string
    isErrorOccured: boolean
    onSnackbarClose: ReactEmptyEvent
    errMessage: string
    onConfirmBtnClick: ReactEmptyEvent
    uiLanguageList: Array<UILanguageProps>
    emailRef: RefObject<any>
    pwdRef: RefObject<any>
    confirmPwdRef: RefObject<any>
    uiLanguage: UILanguageProps
    onUILanguageChange: ReactSelectEvent
    formSubmitBtnRef: RefObject<HTMLButtonElement>
    onSubmit: ReactFormEvent
    onRequestGoing: boolean
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const SystemAdminAdd: FC<SystemAdminAddFCProps> = ({ classes, validator, blurValidator, formatter, ...props }) => {
    const title = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.title_system_admin_add' })
    const labelUserName = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.label_username' })
    const labelDispName = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.label_disp_name' })
    const labelEmail = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.label_email' })
    const labelPassword = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.label_password' })
    const labelConfirmPassword = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.label_confirm_password' })
    const labelUiLang = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.label_ui_lang' })
    const labelDesc = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.label_desc' })
    const btnAddText = Intl.getIntlText({ id: 'htmljs_react.mgmt.systemadminadd.btn_add' })

    return (
        <form id={props.formId} className={classes.Wrapper}>
            <div className={classes.HeaderWrapper}>{title}</div>
            <div className={classes.InputWrapper}>
                <Input
                    id={'username'}
                    type="string"
                    label={labelUserName}
                    required={true}
                    isFullWidth={true}
                    validator={validator!.userNameValidator}
                    blurValidator={blurValidator!.userNameBlurValidator}
                    autoFocus={true}
                    inputProps={{
                        'data-cy-input': 'username'
                    }}
                />
                <Input
                    id={'display_name'}
                    type="string"
                    label={labelDispName}
                    required={true}
                    isFullWidth={true}
                    validator={validator!.displayNameValidator}
                    inputProps={{
                        'data-cy-input': 'display_name'
                    }}
                />
                <Input
                    ref={props.emailRef}
                    id={'email'}
                    type="string"
                    label={labelEmail}
                    isFullWidth={true}
                    required={true}
                    validator={validator!.emailValidator}
                    blurValidator={blurValidator!.emailBlurValidator}
                    inputProps={{
                        'data-cy-input': 'email'
                    }}
                />
                <Input
                    ref={props.pwdRef}
                    id={'password'}
                    label={labelPassword}
                    required={true}
                    isPassword={true}
                    isFullWidth={true}
                    validator={validator!.pwdValidator}
                    blurValidator={blurValidator!.pwdBlurValidator}
                    inputProps={{
                        'data-cy-input': 'password'
                    }}
                />
                <Input
                    ref={props.confirmPwdRef}
                    id={'confirm_pwd'}
                    label={labelConfirmPassword}
                    required={true}
                    isPassword={true}
                    isFullWidth={true}
                    validator={validator!.confirmPwdValidator}
                    blurValidator={blurValidator!.confirmPwdBlurValidator}
                    inputProps={{
                        'data-cy-input': 'confirm_pwd'
                    }}
                />
                <Select
                    id="ui_language"
                    label={labelUiLang}
                    value={props.uiLanguage!.id}
                    menuItems={props.uiLanguageList}
                    onChange={props.onUILanguageChange}
                    isRequired={true}
                    isFullWidth={true}
                    itemFormatter={formatter!.uiLanguageFormatter}
                    inputProps={{
                        'data-cy-input': 'ui_language'
                    }}
                />
                <Input
                    id={'description'}
                    type="string"
                    label={labelDesc}
                    isFullWidth={true}
                    validator={validator!.descValidator}
                    inputProps={{
                        'data-cy-input': 'description'
                    }}
                />

                <div className={classes.BtnSingleWrapper}>
                    <Button
                        btnText={btnAddText}
                        buttonType={'task'}
                        onClick={props.onConfirmBtnClick}
                        disabled={props.onRequestGoing}
                        data-cy-btn={'submit'}
                    />
                </div>
            </div>
            {/** Snack bar for Login Status */}
            <Snackbar
                className={classes.Snackbar}
                open={props.isErrorOccured}
                onClose={props.onSnackbarClose}
                TransitionComponent={Fade}
                autoHideDuration={SNACK_BAR_VALID_TIME}
            >
                <SnackbarContent
                    className={classes.SnackbarContent}
                    evtType="error"
                    noticeFrom="addSystemAdmin"
                    message={props.errMessage}
                    onClose={props.onSnackbarClose}
                />
            </Snackbar>
        </form>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const styles = (theme: Theme) =>
    createStyles({
        ...MgmtConfigFormStyle(theme),
        BtnSingleWrapper: {
            display: 'flex',
            padding: `0 ${theme.spacing() * 2}px ${theme.spacing()}px ${theme.spacing() * 2}px`,
            justifyContent: 'center',
        },
    })

export default withStyles(styles, { withTheme: true })(SystemAdminAdd)
